import { NumberInput, Grid, Button } from "@mantine/core";
import { Modal } from "@mantine/core";
import { useContext, useEffect } from "react";
import { OrderContext } from "../../../context/OrderContext";
import { requiredFieldFormValidator } from "../../../utils/validators/formValidators";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { OrderProduct } from "../interfaces";
import ProductType from "../../../enum/ProductType";
import { useFormContext } from "react-hook-form";
import { generateObjectIdLikeLineId, normalizeAttributesForLineType } from "../utils/lineIdentity";
import { normalizeOrderQuantity, validateOrderQuantity, type OrderQuantityType } from "../utils/orderQuantity";
import { initializePieceCountForNewLine, parseOptionalPieceCount } from "../utils/pieceCount";
import { recalculateOrderProductsWithLineIds } from "../utils/recalculateOrderProducts";

const AddWeightModal = ({ storeDiscounts }: { storeDiscounts: any }) => {
    const { t } = useTranslation(['NewOrder', 'FieldName', 'FormValidator']);
    const {
        setUpdatingProductModal,
        updatingProductModal,
        setUpdatingProduct,
        updatingProduct,
        setLastAddedOrderProduct
    } = useContext(OrderContext);
    const { setValue, getValues } = useFormContext();
    const form = useForm({
        initialValues: {
            quantity: undefined,
            pieceCount: undefined
        },
        validate: {
            quantity: (value) => {
                const requiredValidatorRes = requiredFieldFormValidator(value);
                if (requiredValidatorRes) return requiredValidatorRes;
            }
        }
    });

    type FormValues = typeof form.values;
    const isLengthProduct = updatingProduct?.type === ProductType.LENGTH;
    const orderQuantityType: OrderQuantityType = isLengthProduct ? 'LENGTH' : 'WEIGHT';
    const measuredPrecision = isLengthProduct ? 3 : 2;
    const quantityLabel = isLengthProduct ? t('NewOrder:enterLength') : t('NewOrder:enterWeight');

    const validateQuantityValue = (value: number | string | undefined | null): string | null => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
        const result = validateOrderQuantity(value, orderQuantityType);
        return result.ok ? null : t('FormValidator:invalidQuantity');
    };

    const submit = async (values: FormValues) => {
        const products = getValues('products') || [];
        if (!updatingProduct) return;
        const validationError = validateQuantityValue(values.quantity);
        if (validationError) {
            form.setFieldError('quantity', validationError);
            return;
        }
        const normalizedQuantity = normalizeOrderQuantity(values.quantity, orderQuantityType);
        if (normalizedQuantity === null) {
            form.setFieldError('quantity', t('FormValidator:invalidQuantity'));
            return;
        }
        const parsedPieceCount = parseOptionalPieceCount(values.pieceCount);
        if (!parsedPieceCount.ok) {
            form.setFieldError('pieceCount', t('FormValidator:invalidQuantity'));
            return;
        }

        if (normalizedQuantity === 0) {
            setUpdatingProductModal(false);
            setUpdatingProduct(undefined);
            return;
        }

        const lineId = updatingProduct.lineId || updatingProduct.orderProductLineID || generateObjectIdLikeLineId();
        const newLine: OrderProduct = {
            ...initializePieceCountForNewLine({
                ...updatingProduct,
                lineId,
                orderProductLineID: lineId,
                productId: updatingProduct.productId || updatingProduct._id || '',
                extraAmount: updatingProduct.extraAmount || 0,
                discountAmount: updatingProduct.discountAmount || 0,
                properties: updatingProduct.properties || [],
                attributes: normalizeAttributesForLineType(updatingProduct, 1),
                quantity: normalizedQuantity,
                price: updatingProduct.price,
                name: updatingProduct.name,
                expressPrice: updatingProduct.expressPrice,
                type: updatingProduct.type || ProductType.WEIGHT,
                taxExemptOne: updatingProduct.taxExemptOne,
                taxExemptTwo: updatingProduct.taxExemptTwo,
                taxExemptThree: updatingProduct.taxExemptThree,
                selectedOptionId: updatingProduct.selectedOptionId || null,
                selectedOption: updatingProduct.selectedOption || undefined
            }),
            pieceCount: parsedPieceCount.value,
            __pieceCountTouched: parsedPieceCount.value !== null
        };

        const productsCopy = [...products, newLine];
        const { newOrderProds, buyAndGetProds } = recalculateOrderProductsWithLineIds({
            discountCodeObj: getValues('discountCodeObj'),
            products: productsCopy,
            express: getValues('express'),
            storeDiscounts,
            customerInfo: getValues('customerInfo'),
            previousBuyAndGetProducts: getValues('buyAndGetProducts'),
            orderType: getValues('orderType'),
            returnItemsChargeMode: getValues('returnItemsChargeMode')
        });
        setValue('products', newOrderProds);
        setValue('buyAndGetProducts', buyAndGetProds);
        setLastAddedOrderProduct(newLine);
        setUpdatingProductModal(false);
        setUpdatingProduct(undefined);
    }

    useEffect(() => {
        form.setValues({
            quantity: undefined,
            pieceCount: undefined
        });
        form.setErrors({});
    }, [updatingProduct]);

    return (
        <Modal
            data-testid="add-weight-modal"
            title={`${quantityLabel}: ${updatingProduct?.name}`}
            opened={updatingProductModal}
            onClose={() => {
                setUpdatingProductModal(false);
                setUpdatingProduct(undefined);
            }}>
            <form onSubmit={form.onSubmit(submit)}>
                <Grid>
                    <Grid.Col span={12}>
                        <NumberInput
                            data-testid="add-weight-quantity"
                            {...form.getInputProps('quantity')}
                            placeholder={quantityLabel}
                            label={t('FieldName:quantity')}
                            allowNegative
                            decimalScale={measuredPrecision}
                            allowedDecimalSeparators={['.']}
                            decimalSeparator="."
                            autoFocus
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <NumberInput
                            data-testid="add-weight-piece-count"
                            {...form.getInputProps('pieceCount')}
                            placeholder={t('FieldName:pieceCountOptional')}
                            label={t('FieldName:pieceCountOptional')}
                            allowDecimal={false}
                            allowNegative={false}
                            min={0}
                            decimalScale={0}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                        <Button
                            fullWidth
                            color="gray"
                            onClick={() => {
                                setUpdatingProductModal(false);
                                setUpdatingProduct(undefined);
                            }}>
                            {t('CommonButtons:cancel')}
                        </Button>
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                        <Button data-testid="add-weight-submit" fullWidth type="submit" >
                            {t('CommonButtons:submit')}
                        </Button>
                    </Grid.Col>
                </Grid>
            </form>

        </Modal>
    );
};

export default AddWeightModal;
